import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, canAccessPatientRegistration } from '../../utils/auth';
import DashboardLayout from '../../components/feature/DashboardLayout';
import RegistrationForm from './components/RegistrationForm';
import PatientList from './components/PatientList';

type Tab = 'register' | 'list';

export default function PatientRegistration() {
  const navigate = useNavigate();
  const user = getUser();
  const canRegister = user?.role === 'patient_registration' || user?.role === 'admin' || user?.role === 'master_admin';
  const [activeTab, setActiveTab] = useState<Tab>(canRegister ? 'register' : 'list');

  useEffect(() => {
    if (!user || !canAccessPatientRegistration(user)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  if (!user) return null;

  const tabs: { key: Tab; label: string; icon: string; roleRestricted: boolean }[] = [
    { key: 'register', label: 'New Registration', icon: 'ri-user-add-line', roleRestricted: true },
    { key: 'list', label: 'Patient List', icon: 'ri-list-check-2', roleRestricted: false },
  ];

  const visibleTabs = tabs.filter((t) => {
    if (t.roleRestricted && !canRegister) return false;
    return true;
  });

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Patient Registration</h2>
          <p className="text-sm text-gray-500 mt-1">
            {canRegister
              ? 'Register new patients and view existing records'
              : 'View registered patient records'}
          </p>
        </div>

        {visibleTabs.length > 1 && (
          <div className="flex gap-1 bg-gray-100 p-1 rounded-full w-fit mb-6">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-white text-teal-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === 'register' && canRegister && <RegistrationForm />}
        {activeTab === 'list' && <PatientList />}
      </div>
    </DashboardLayout>
  );
}
